import React from 'react';
// NON-RELATIVE ON PURPOSE -- do not "tidy" this back to './Navigation'.
//
// PageLayout is shared code: the satellite PWAs (shop-client, client-tech) mount
// client/src pages through their `@shared` alias and deliberately substitute a
// null Navigation stub, because the real one pulls ModuleContext ->
// modules/registry.ts -> every module's routes -> every page in OfficeHub.
// Their Vite plugins can redirect this import, but `tsc` does not run Vite
// plugins and tsconfig `paths` can only remap NON-RELATIVE specifiers -- so a
// relative './Navigation' was unremappable and dragged 2,916 client/src files
// into shop-client's type closure, whose bare imports a shop-only CI install
// cannot resolve (1,543 TS2307 + ~6,000 cascading errors).
//
// Written as a CRA absolute import (client/tsconfig.json sets baseUrl: "src",
// which react-scripts turns into a webpack resolve.modules entry and a Jest
// modulePaths entry), so the main app resolves the REAL Navigation with no extra
// config, while each satellite maps this exact specifier in its own tsconfig
// paths + Vite alias.
import { Navigation } from 'components/Navigation';
import {
  useNavigationRendered,
  NavigationRenderedProvider,
} from '../contexts/NavigationContext';

interface PageLayoutProps {
  children: React.ReactNode;
  /** Use full viewport width instead of max-w-7xl constraint */
  fullWidth?: boolean;
  /** Background color class. Defaults to 'bg-slate-50' */
  bg?: string;
  /** Extra className on the content wrapper */
  className?: string;
  /** Render a full-bleed header band above the content area */
  header?: React.ReactNode;
  /**
   * Content width when not `fullWidth`. Defaults to `7xl`. `screen-2xl` is the Field Service
   * frame (ticket and generator pages); narrower widths suit forms and settings.
   */
  maxWidth?: PageMaxWidth;
  /**
   * The element that wraps the page content. `main` gives screen readers the page's main
   * landmark; the default `div` keeps every existing page's output unchanged.
   */
  as?: 'div' | 'main';
}

export type PageMaxWidth = '3xl' | '5xl' | '7xl' | 'screen-2xl';

/** Literal class names, so Tailwind sees every one of them. */
const MAX_WIDTH_CLASS: Record<PageMaxWidth, string> = {
  '3xl': 'max-w-3xl',
  '5xl': 'max-w-5xl',
  '7xl': 'max-w-7xl',
  'screen-2xl': 'max-w-screen-2xl',
};

/**
 * Shared page shell — Navigation + constrained or full-width content area.
 *
 * Default: max-w-7xl centered (matches existing pages).
 * fullWidth: content stretches to viewport edges with minimal padding.
 *
 * When rendered inside a ModuleLayout (route-level), Navigation is already
 * present, so PageLayout skips it and only renders the content wrapper. This preserves fullWidth / header /
 * bg props while preventing duplicate navigation bars.
 *
 * Usage:
 *   <PageLayout>...</PageLayout>                    // standard width
 *   <PageLayout fullWidth>...</PageLayout>           // full viewport width
 *   <PageLayout header={<MyHeader />}>...</PageLayout> // with sticky header
 *   <PageLayout maxWidth="screen-2xl">...</PageLayout> // Field Service frame
 */
export function PageLayout({
  children,
  fullWidth = false,
  bg = 'bg-slate-50',
  className = '',
  header,
  maxWidth = '7xl',
  as: Content = 'div',
}: PageLayoutProps) {
  const navAlreadyRendered = useNavigationRendered();

  const contentClasses = fullWidth
    ? 'w-full px-4 sm:px-6 py-6'
    : `${MAX_WIDTH_CLASS[maxWidth]} mx-auto px-4 sm:px-6 lg:px-8 py-6`;

  // If Navigation was already rendered by a parent ModuleLayout, skip the
  // nav chrome but still render the content wrapper so fullWidth / header /
  // bg / className continue to work.
  if (navAlreadyRendered) {
    return (
      <div className={`min-h-screen ${bg}`}>
        {header}
        <Content className={`${contentClasses} ${className}`}>
          {children}
        </Content>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg}`}>
      <Navigation />
      <NavigationRenderedProvider value={true}>
        {header}
        <Content className={`${contentClasses} ${className}`}>
          {children}
        </Content>
      </NavigationRenderedProvider>
    </div>
  );
}
